import React, { useRef, useState } from 'react';
import { Camera, Upload, X, Loader2, FileText } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

/**
 * FileUpload — button that opens phone camera (image) or file picker (image/pdf).
 * On select, uploads to POST /api/files and calls onChange({ url, id, mime }).
 * Shows preview thumbnail for images / file icon for PDFs.
 */
const FileUpload = ({
  value,            // { url, mime } | string url
  onChange,
  label = 'Upload',
  accept = 'image/*',
  capture = false,  // true to use phone camera
  placeholder = 'Tap to upload',
}) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const url = typeof value === 'string' ? value : value?.url;
  const mime = typeof value === 'object' ? value?.mime : null;
  const isPdf = mime === 'application/pdf' || (url && url.endsWith('.pdf'));

  const compressImage = (file) => new Promise((resolve) => {
    if (!file.type.startsWith('image/')) return resolve(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const max = 1600;
        let w = img.width, h = img.height;
        if (w > max || h > max) {
          if (w > h) { h = (h * max) / w; w = max; } else { w = (w * max) / h; h = max; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        canvas.toBlob((blob) => {
          resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }));
        }, 'image/jpeg', 0.82);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024 && !f.type.startsWith('image/')) {
      toast.error('File is over 5 MB'); return;
    }
    setBusy(true);
    try {
      const compressed = await compressImage(f);
      const fd = new FormData();
      fd.append('file', compressed);
      const r = await api.post('/files', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange?.(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const clear = (e) => { e.stopPropagation(); onChange?.(null); };

  return (
    <div>
      {label && <div className="text-xs text-slate-600 mb-1">{label}</div>}
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className="w-full h-32 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 hover:border-emerald-400 hover:bg-emerald-50/40 transition-colors flex flex-col items-center justify-center gap-1.5 relative overflow-hidden"
      >
        {busy ? (
          <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
        ) : url ? (
          <>
            {isPdf ? (
              <div className="flex flex-col items-center gap-1 text-slate-700">
                <FileText className="h-8 w-8 text-rose-500" />
                <span className="text-xs font-medium">PDF uploaded</span>
              </div>
            ) : (
              <img src={url} alt="preview" className="absolute inset-0 w-full h-full object-cover" />
            )}
            <button type="button" onClick={clear} className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-white/95 hover:bg-white text-slate-700 flex items-center justify-center shadow z-10">
              <X className="h-3.5 w-3.5" />
            </button>
          </>
        ) : (
          <>
            {capture ? <Camera className="h-6 w-6 text-slate-400" /> : <Upload className="h-6 w-6 text-slate-400" />}
            <span className="text-xs text-slate-500">{placeholder}</span>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          {...(capture ? { capture: 'environment' } : {})}
          onChange={handle}
          className="hidden"
        />
      </button>
    </div>
  );
};

export default FileUpload;
